import type { LocalizedCopy } from "../productCopyTypes";

function paragraphs(text: string): string {
  return text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>\n${p}\n</p>`)
    .join("\n\n");
}

function featureList(items: string[]): string {
  return `<ul>\n${items.map((item) => `  <li>${item}</li>`).join("\n")}\n</ul>`;
}

const COPY = {
  en: {
    title: "Car series",
    description:
      "Step into the world of classic sci-fi adventures with the 12GO 95007 Building Block Car. This impressive set features two iconic vehicles - a blue utility van equipped with a signature rooftop device and a red classic car with its own specialized equipment. With 1,060 high-quality building pieces, this set offers a rewarding building experience and an eye-catching display model.\n\nDesigned with great attention to detail, both vehicles feature realistic interiors complete with seats, steering wheels, dashboards, and additional accessories. The opening doors and removable roof sections make it easy to explore the detailed cabins, while the modular construction allows you to showcase the interior or reassemble the models with ease.\n\nThe distinctive rooftop equipment gives each vehicle its unmistakable appearance, making this set a perfect centerpiece for any collection. Whether displayed on a shelf or desk, both models look fantastic from every angle thanks to their accurate proportions and clean design.",
    highlightsTitle: "Key Features",
    features: [
      "1,060 high-quality building pieces",
      "Includes two classic vehicles in one set",
      "Modular construction with removable roof sections",
      "Opening doors for easy access to the interior",
      "Highly detailed interiors with realistic accessories",
      "Signature rooftop equipment on both vehicles",
      "Compatible with building bricks from other major brands",
      "Perfect for collectors, display enthusiasts, and sci-fi fans",
      "An enjoyable building experience for older children and adult builders",
    ],
    specsTitle: "Product Features",
    specs: [
      "<li><strong>Brand:</strong> 12GO</li>",
      "<li><strong>Model:</strong> Building Block Car</li>",
      "<li><strong>Item Number:</strong> 95007</li>",
      "<li><strong>Piece Count:</strong> 1060+ pieces</li>",
      "<li><strong>Recommended Age:</strong> 6+</li>",
    ],
  },
  pl: {
    title: "Car series",
    description:
      "Przenieś się do świata klasycznych przygód science fiction z zestawem 12GO 95007 Building Block Car. Ten wyjątkowy komplet zawiera dwa charakterystyczne pojazdy - niebieskiego vana z kultowym urządzeniem na dachu oraz czerwony klasyczny samochód wyposażony we własny moduł badawczy. Zestaw składa się z 1060 wysokiej jakości elementów, zapewniając satysfakcjonującą budowę i efektowny model kolekcjonerski.\n\nOba pojazdy zostały zaprojektowane z dużą dbałością o detale. Wnętrza wyposażono w fotele, kierownice, deski rozdzielcze i liczne akcesoria. Otwierane drzwi oraz zdejmowane sekcje dachu umożliwiają wygodne podziwianie wnętrza, a modułowa konstrukcja ułatwia prezentację modeli.\n\nCharakterystyczne urządzenia zamontowane na dachach nadają pojazdom wyjątkowy wygląd. Modele doskonale prezentują się na półce lub biurku i stanowią świetny element każdej kolekcji.",
    highlightsTitle: "Najważniejsze cechy",
    features: [
      "1060 wysokiej jakości elementów",
      "Dwa klasyczne pojazdy w jednym zestawie",
      "Modułowa konstrukcja ze zdejmowanymi sekcjami dachu",
      "Otwierane drzwi zapewniające dostęp do wnętrza",
      "Bogato odwzorowane wnętrza z realistycznymi detalami",
      "Charakterystyczne wyposażenie dachowe obu pojazdów",
      "Kompatybilność z klockami innych popularnych producentów",
      "Idealny dla kolekcjonerów i fanów science fiction",
      "Przyjemna budowa dla starszych dzieci i dorosłych",
    ],
    specsTitle: "Cechy zestawu",
    specs: [
      "<li><strong>Marka:</strong> 12GO</li>",
      "<li><strong>Model:</strong> Building Block Car</li>",
      "<li><strong>Numer katalogowy:</strong> 95007</li>",
      "<li><strong>Liczba elementów:</strong> 1060+</li>",
      "<li><strong>Wiek zalecany:</strong> 6+</li>",
    ],
  },
  es: {
    title: "Car series",
    description:
      "Adéntrate en el mundo de las clásicas aventuras de ciencia ficción con el 12GO 95007 Building Block Car. Este impresionante set incluye dos vehículos icónicos: una furgoneta azul equipada con un característico dispositivo en el techo y un automóvil clásico rojo con su propio equipo especializado. Con 1060 piezas de alta calidad, ofrece una experiencia de construcción entretenida y un espectacular modelo de exhibición.\n\nAmbos vehículos cuentan con interiores detallados que incluyen asientos, volante, tablero y diversos accesorios. Las puertas abatibles y las secciones desmontables del techo permiten explorar fácilmente el interior, mientras que la construcción modular facilita su exposición.\n\nEl equipamiento del techo aporta un aspecto inconfundible a cada vehículo, convirtiendo este set en una pieza ideal para cualquier colección.",
    highlightsTitle: "Características destacadas",
    features: [
      "1060 piezas de alta calidad",
      "Dos vehículos clásicos en un solo set",
      "Construcción modular con techo desmontable",
      "Puertas que se pueden abrir",
      "Interiores detallados con accesorios realistas",
      "Equipamiento característico en el techo",
      "Compatible con otras marcas populares de bloques",
      "Ideal para coleccionistas y aficionados a la ciencia ficción",
      "Experiencia de construcción para niños mayores y adultos",
    ],
    specsTitle: "Características del producto",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Modelo:</strong> Building Block Car</li>",
      "<li><strong>Número de artículo:</strong> 95007</li>",
      "<li><strong>Piezas:</strong> 1060+</li>",
      "<li><strong>Edad recomendada:</strong> 6+</li>",
    ],
  },
  de: {
    title: "Car series",
    description:
      "Tauche ein in die Welt klassischer Science-Fiction-Abenteuer mit dem 12GO 95007 Building Block Car. Dieses beeindruckende Set enthält zwei ikonische Fahrzeuge - einen blauen Van mit charakteristischer Dachausrüstung und einen roten klassischen Wagen mit eigener Spezialausstattung. Mit 1060 hochwertigen Bausteinen bietet das Set ein spannendes Bauerlebnis und ein beeindruckendes Ausstellungsmodell.\n\nBeide Fahrzeuge verfügen über detailreiche Innenräume mit Sitzen, Lenkrad, Armaturenbrett und weiterem Zubehör. Öffnende Türen und abnehmbare Dachsegmente ermöglichen einen einfachen Blick ins Innere, während die modulare Bauweise vielseitige Präsentationsmöglichkeiten bietet.\n\nDie markanten Dachaufbauten verleihen den Fahrzeugen ihren unverwechselbaren Look und machen das Set zu einem Highlight jeder Sammlung.",
    highlightsTitle: "Wichtige Merkmale",
    features: [
      "1060 hochwertige Bausteine",
      "Zwei klassische Fahrzeuge in einem Set",
      "Modulare Konstruktion mit abnehmbarem Dach",
      "Öffnende Türen",
      "Detailreiche Innenausstattung",
      "Charakteristische Dachausrüstung",
      "Kompatibel mit Bausteinen anderer Hersteller",
      "Ideal für Sammler und Science-Fiction-Fans",
      "Bauspaß für Jugendliche und Erwachsene",
    ],
    specsTitle: "Produkteigenschaften",
    specs: [
      "<li><strong>Marke:</strong> 12GO</li>",
      "<li><strong>Modell:</strong> Building Block Car</li>",
      "<li><strong>Artikelnummer:</strong> 95007</li>",
      "<li><strong>Teileanzahl:</strong> 1060+</li>",
      "<li><strong>Empfohlenes Alter:</strong> 6+</li>",
    ],
  },
  fr: {
    title: "Car series",
    description:
      "Plongez dans l'univers des aventures classiques de science-fiction avec le 12GO 95007 Building Block Car. Ce superbe ensemble comprend deux véhicules emblématiques : un fourgon bleu équipé d'un dispositif caractéristique sur le toit et une voiture classique rouge avec son propre équipement spécialisé. Avec 1060 pièces de haute qualité, ce set offre une expérience de construction passionnante et un magnifique modèle d'exposition.\n\nLes deux véhicules disposent d'intérieurs détaillés avec sièges, volant, tableau de bord et divers accessoires. Les portes ouvrantes et les sections de toit amovibles permettent d'explorer facilement l'intérieur, tandis que la conception modulaire facilite la présentation.\n\nLes équipements montés sur le toit donnent à chaque véhicule un style unique, faisant de cet ensemble une pièce idéale pour toute collection.",
    highlightsTitle: "Points forts",
    features: [
      "1060 pièces de haute qualité",
      "Deux véhicules classiques dans un seul coffret",
      "Construction modulaire avec toit amovible",
      "Portes ouvrantes",
      "Intérieurs détaillés",
      "Équipements caractéristiques sur le toit",
      "Compatible avec les briques d'autres grandes marques",
      "Parfait pour les collectionneurs et les fans de science-fiction",
      "Une construction agréable pour les adolescents et les adultes",
    ],
    specsTitle: "Caractéristiques",
    specs: [
      "<li><strong>Marque :</strong> 12GO</li>",
      "<li><strong>Modèle :</strong> Building Block Car</li>",
      "<li><strong>Référence :</strong> 95007</li>",
      "<li><strong>Nombre de pièces :</strong> 1060+</li>",
      "<li><strong>Âge recommandé :</strong> 6+</li>",
    ],
  },
  nl: {
    title: "Car series",
    description:
      "Stap in de wereld van klassieke sciencefictionavonturen met de 12GO 95007 Building Block Car. Deze indrukwekkende set bevat twee iconische voertuigen: een blauwe bestelwagen met een karakteristiek dakapparaat en een rode klassieke auto met eigen speciale uitrusting. Met 1060 hoogwaardige bouwstenen biedt deze set een plezierige bouwervaring en een prachtig displaymodel.\n\nBeide voertuigen beschikken over gedetailleerde interieurs met stoelen, stuur, dashboard en diverse accessoires. De openslaande deuren en afneembare dakdelen maken het eenvoudig om het interieur te bekijken, terwijl de modulaire constructie zorgt voor flexibele presentatie.\n\nDe opvallende dakuitrusting geeft beide voertuigen hun unieke uitstraling en maakt deze set tot een perfecte aanvulling op iedere verzameling.",
    highlightsTitle: "Belangrijkste kenmerken",
    features: [
      "1060 hoogwaardige bouwstenen",
      "Twee klassieke voertuigen in één set",
      "Modulaire constructie met afneembaar dak",
      "Openende deuren",
      "Gedetailleerde interieurs",
      "Karakteristieke dakuitrusting",
      "Compatibel met bouwstenen van andere bekende merken",
      "Perfect voor verzamelaars en sciencefictionliefhebbers",
      "Leuke bouwervaring voor oudere kinderen en volwassenen",
    ],
    specsTitle: "Productkenmerken",
    specs: [
      "<li><strong>Merk:</strong> 12GO</li>",
      "<li><strong>Model:</strong> Building Block Car</li>",
      "<li><strong>Artikelnummer:</strong> 95007</li>",
      "<li><strong>Aantal onderdelen:</strong> 1060+</li>",
      "<li><strong>Aanbevolen leeftijd:</strong> 6+</li>",
    ],
  },
  zh: {
    title: "Car series",
    description:
      "12GO 95007 积木汽车带您进入经典科幻冒险的世界。套装包含两辆标志性车型：一辆配备特色车顶装置的蓝色厢式车，以及一辆搭载专用设备的红色经典轿车。全套共1060块优质积木，带来丰富的拼搭乐趣和极具观赏价值的展示模型。\n\n两辆车型均拥有精致的内部细节，包括座椅、方向盘、仪表盘以及丰富的配件。可开启车门和可拆卸车顶方便展示内部结构，模块化设计也让模型更易于展示和组装。\n\n独特的车顶设备赋予两辆车型鲜明的外观，是科幻主题收藏中不可错过的一款作品。",
    highlightsTitle: "套装亮点",
    features: [
      "1060块优质积木",
      "一套包含两辆经典车型",
      "模块化设计，可拆卸车顶",
      "可开启车门",
      "精致的内部细节",
      "标志性的车顶设备",
      "兼容其他主流积木品牌",
      "适合收藏爱好者和科幻迷",
      "适合大龄儿童及成人拼搭",
    ],
    specsTitle: "产品特点",
    specs: [
      "<li><strong>品牌：</strong>12GO</li>",
      "<li><strong>型号：</strong>Building Block Car</li>",
      "<li><strong>货号：</strong>95007</li>",
      "<li><strong>颗粒数：</strong>1060+</li>",
      "<li><strong>建议年龄：</strong>6+</li>",
    ],
  },
  it: {
    title: "Serie di auto",
    description:
      "Entra nel mondo delle classiche avventure fantascientifiche con l'auto con mattoncini da costruzione 12GO 95007. Questo straordinario set include due veicoli iconici: un furgone blu dotato di un caratteristico dispositivo sul tetto e un'auto classica rossa con la propria attrezzatura specializzata. Con 1,060 mattoncini di alta qualità, il set offre un'esperienza di costruzione appagante e un modello da esposizione davvero suggestivo.\n\nProgettati con grande attenzione ai dettagli, entrambi i veicoli presentano interni realistici completi di sedili, volanti, cruscotti e accessori aggiuntivi. Le portiere apribili e le sezioni del tetto rimovibili permettono di esplorare facilmente gli abitacoli dettagliati, mentre la struttura modulare consente di esporre gli interni o riassemblare i modelli con facilità.\n\nLe caratteristiche attrezzature sul tetto conferiscono a ciascun veicolo un aspetto inconfondibile, rendendo questo set il pezzo forte di qualsiasi collezione. Sia su uno scaffale sia su una scrivania, entrambi i modelli fanno un'ottima figura da ogni angolazione grazie alle proporzioni accurate e al design pulito.",
    highlightsTitle: "Caratteristiche principali",
    features: [
      "1,060 mattoncini di costruzione di alta qualità",
      "Include due veicoli classici in un unico set",
      "Struttura modulare con sezioni del tetto rimovibili",
      "Portiere apribili per accedere facilmente agli interni",
      "Interni ricchi di dettagli con accessori realistici",
      "Caratteristiche attrezzature sul tetto di entrambi i veicoli",
      "Compatibile con i mattoncini da costruzione di altri importanti marchi",
      "Perfetto per collezionisti, appassionati di modellini da esposizione e fan della fantascienza",
      "Un'esperienza di costruzione divertente per ragazzi più grandi e adulti",
    ],
    specsTitle: "Caratteristiche del prodotto",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Modello:</strong> Auto con mattoncini da costruzione</li>",
      "<li><strong>Numero articolo:</strong> 95007</li>",
      "<li><strong>Numero di pezzi:</strong> 1060+ pezzi</li>",
      "<li><strong>Età consigliata:</strong> 6+</li>",
    ],
  },
  ru: {
    title: "Серия автомобилей",
    description:
      "Окунитесь в мир классических научно-фантастических приключений с конструктором автомобиля 12GO 95007. В этот впечатляющий набор входят два культовых транспортных средства: синий фургон с характерным устройством на крыше и красный классический автомобиль со специальным оборудованием. В наборе 1,060 качественных деталей, которые подарят увлекательный процесс сборки и позволят создать эффектную модель для демонстрации.\n\nОба автомобиля выполнены с большим вниманием к деталям и оснащены реалистичными интерьерами с сиденьями, рулевыми колёсами, приборными панелями и дополнительными аксессуарами. Открывающиеся двери и съёмные секции крыши позволяют легко рассмотреть детализированные салоны, а модульная конструкция даёт возможность демонстрировать интерьер или без труда собирать модели заново.\n\nХарактерное оборудование на крыше придаёт каждому автомобилю неповторимый вид, благодаря чему набор станет центральным элементом любой коллекции. На полке или на рабочем столе обе модели отлично смотрятся с любого ракурса благодаря точным пропорциям и лаконичному дизайну.",
    highlightsTitle: "Основные особенности",
    features: [
      "1,060 качественных деталей конструктора",
      "В набор входят два классических автомобиля",
      "Модульная конструкция со съёмными секциями крыши",
      "Открывающиеся двери для удобного доступа к салону",
      "Детализированные интерьеры с реалистичными аксессуарами",
      "Характерное оборудование на крыше обоих автомобилей",
      "Совместимость с конструкторами других известных брендов",
      "Отличный выбор для коллекционеров, любителей моделей для демонстрации и поклонников научной фантастики",
      "Увлекательный процесс сборки для детей старшего возраста и взрослых",
    ],
    specsTitle: "Характеристики товара",
    specs: [
      "<li><strong>Бренд:</strong> 12GO</li>",
      "<li><strong>Модель:</strong> Автомобиль из деталей конструктора</li>",
      "<li><strong>Номер изделия:</strong> 95007</li>",
      "<li><strong>Количество деталей:</strong> 1060+ деталей</li>",
      "<li><strong>Рекомендуемый возраст:</strong> 6+</li>",
    ],
  },

} as const;

function buildLocaleHtml(locale: keyof typeof COPY): string {
  const c = COPY[locale];
  return `<h1>${c.title}</h1>

${paragraphs(c.description)}

<h2>${c.specsTitle}</h2>

<ul>
${c.specs.join("\n")}
</ul>

<h2>${c.highlightsTitle}</h2>

${featureList([...c.features])}`;
}

export const RICH_PRODUCT_COPY_95007: LocalizedCopy = {
  en: buildLocaleHtml("en"),
  pl: buildLocaleHtml("pl"),
  de: buildLocaleHtml("de"),
  es: buildLocaleHtml("es"),
  fr: buildLocaleHtml("fr"),
  nl: buildLocaleHtml("nl"),
  zh: buildLocaleHtml("zh"),
  it: buildLocaleHtml("it"),
  ru: buildLocaleHtml("ru"),
};
